import { useState } from 'react';
import { useFadeIn } from '@/hooks/useFadeIn';

const faqs = [
  {
    q: '痛みはありますか?',
    a: '治療中の痛みはほとんどありません。ジェルが歯茎に触れると軽いしみる感覚を覚えることがありますが、麻酔は不要です。',
  },
  {
    q: '完全に白い点がなくなりますか?',
    a: '白斑の深さや範囲によって異なります。多くの方で大幅に改善しますが、完全に消えない場合もあります。事前のカウンセリングで改善度の目安をご説明します。',
  },
  {
    q: '効果はどのくらい続きますか?',
    a: '適切なホームケアを続けていただければ、長期的に安定した状態を保てます。時間経過による色の変化は非常に小さいとされています。',
  },
  {
    q: '治療後に食事や生活の制限はありますか?',
    a: '特別な制限はありません。当日から通常通りの食事が可能です。長期的な状態を保つために、定期的なクリーニングをおすすめしています。',
  },
  {
    q: '矯正治療中でも受けられますか?',
    a: '矯正装置が装着中の場合は対応が難しいケースがあります。装置を外した後のタイミングでのご相談をおすすめします。まずはカウンセリングでご確認ください。',
  },
];

interface FaqItemProps {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqItem({ faq, index, isOpen, onToggle }: FaqItemProps) {
  const fade = useFadeIn({ direction: 'up', delay: index * 60 });
  return (
    <div
      ref={fade.ref}
      style={fade.style}
      className="bg-white rounded-lg border-b border-gray-200 overflow-hidden"
    >
      <button
        className="w-full flex items-start gap-3 p-4 md:p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white font-medium text-xs flex-shrink-0 mt-0.5"
          style={{ backgroundColor: '#3E5F8F' }}
        >
          Q
        </span>
        <span className="font-medium text-gray-700 text-sm md:text-base leading-relaxed flex-1">
          {faq.q}
        </span>
        <i
          className="ri-arrow-down-s-line text-gray-500 text-lg flex-shrink-0 transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        ></i>
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{ maxHeight: isOpen ? '300px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        <div className="flex items-start gap-3 px-4 md:px-5 pb-4 md:pb-5">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-400 text-white font-medium text-xs flex-shrink-0 mt-0.5">
            A
          </span>
          <p className="font-light text-gray-700 text-sm leading-loose">{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function IconFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const heading = useFadeIn({ direction: 'up' });

  return (
    <section id="faq" className="bg-white py-14 md:py-24">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style}>
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            FAQ
          </span>
          <div className="w-8 h-px mt-3 mb-4" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-3xl leading-relaxed mb-8 md:mb-10">
            よくあるご質問
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}